<template>
    <div id="print-queue-container">
        <card title="Active Prescription Queue">
            <div class="filter-grid mb-3">
                <SearchComponent v-model="storeFilter" :label="`Store`" :search-u-r-l="`/Store`" :params="{active: true}" :prepopulate="true"
                    :disabled="isLoading" clearable />

                <SearchComponent v-model="programFilter" label="Program" search-u-r-l="/Program"
                    :params="{ active: true }" clearable prepopulate :disabled="isLoading" />

                <div>
                    <div class="d-flex justify-content-between align-items-center">
                        <label class="mb-0">Statuses:</label>
                        <div class="d-flex align-items-center">
                            <b-check v-model="showAllStatuses" switch :disabled="isLoading">
                                <span style="margin-top: 2px;display:inline-block;">
Show all statuses
                                </span>
                                
                            </b-check>
                            <HelpPopup>
                                <p>By default, the Active Queue excludes prescriptions with certain statuses that are typically not actionable:</p>
                                <p><strong>Excluded Statuses:</strong> Transfer Out, Expired, Void, Discontinued</p>
                                <p><strong>Excluded Substatus:</strong> Rx Denied</p>
                                <p>Enable this switch to include these statuses in the queue and make them available in the filter dropdowns.</p>
                            </HelpPopup>
                        </div>
                    </div>
                    <multiselect v-model="statuses" :options="filteredStatusCodeOptions" multiple label="name" track-by="value"
                        :limit="2" :disabled="isLoading" />
                </div>

                <div>
                    <label>SubStatuses:</label>
                    <multiselect v-model="substatuses" :options="filteredSubStatusCodeOptions" multiple label="name"
                        track-by="value" :limit="2" :disabled="isLoading" />
                </div>

                <div>
                    <label>Fill Date:</label>
                    <div class="d-flex" style="gap: 15px;">
                        <el-date-picker size="sm" v-model="startDate" today-button reset-button :disabled="isLoading" />
                        <el-date-picker size="sm" v-model="endDate" today-button reset-button :disabled="isLoading" />
                    </div>

                </div>
            </div>

            <PrescriptionsListComponent :defaultSort="sortBy" :prescriptions="prescriptions" showPatientName
                alwaysShowVoid defaultDesc :showSelectCheckox="false" :isLoading="isLoading" :actionButtons="['edit']"
                :additional-fields="additionalFields" color-code-prescriptions
                :manualSort="['productNameShort', 'patientName', 'fillDate', 'storeName','rxId', 'dispensed',
                'programName', 'patientPay']"
                @sort-changed="handleSort" />

            <BottomPagerBar v-model="pageNumber" :total-rows="totalRows" :per-page="pageSize" show-total
                show-page-size-selector item-name="prescription" @change-page-size="pageSize = $event"
                :loading="isLoading" />

        </card>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { DatePicker } from "element-ui";
import SearchComponent from '@/components/SearchComponent.vue';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import { Prescription, RefillStatusCode } from '@/models/Prescription';
import { Store } from '@/models/Store';
import HelpPopup from "@/components/HelpPopup.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import Multiselect from 'vue-multiselect';
import moment from 'moment';
import { Program } from "@/models/Program";
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import PhoneFormatter from '@/util/PhoneFormatter';

@Component({
    name: "ActiveQueue",
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        HelpPopup,
        Multiselect,
        BottomPagerBar
    },
})
export default class ActiveQueue extends Vue {
    protected prescriptions: Array<Prescription> = [];
    private storeFilter: Store = new Store();
    private programFilter: Program = new Program();
    protected startDate?: string = moment().subtract(14, 'days').toISOString(); // Default to 14 days ago
    protected endDate?: string = moment().toISOString();

    protected isLoading: boolean = false;

    protected totalRows: number = 0;
    protected pageNumber: number = 1;
    protected pageSize: number = 50;
    protected statuses: Array<any> = [];
    protected substatuses: Array<any> = [];
    // SD-1753: Checkbox to show/hide excluded statuses (TransferOut, Expired, Void, Discontinued) and RxDenied substatus
    protected showAllStatuses: boolean = false;

    private sortBy: string = "fillDate";
    private sortDirection: string = "D";

    private additionalFields = [
        { index: 3, field: { key: 'patientPhone', formatter: this.phoneFormatter } },
        { index: 4, field: { key: 'patientCell', formatter: this.phoneFormatter } },
        { index: 5, field: { key: 'addressState', label: 'Patient State' } },
    ];

    phoneFormatter(value: string, key: string, item: any) {
        return PhoneFormatter.formatForDisplay(value);
    }
    get startRowIndex() {
        return this.pageNumber * this.pageSize - this.pageSize;
    }

    get endRowIndex() {
        return this.startRowIndex + this.prescriptions.length;
    }

    get statusCodeOptions() {
        return Object.keys(RefillStatusCode).filter(key => isNaN(Number(key)))
            .map(key => ({
                name: key,
                value: (RefillStatusCode as any)[key]
            }))
    }

    // SD-1753: Excluded statuses - TransferOut(4), Expired(8), Void(9), Discontinued(11)
    private excludedStatuses = [
        RefillStatusCode.TransferOut,
        RefillStatusCode.Expired,
        RefillStatusCode.Void,
        RefillStatusCode.Discontinued
    ];

    get filteredStatusCodeOptions() {
        if (this.showAllStatuses) {
            return this.statusCodeOptions;
        }
        return this.statusCodeOptions.filter(s => !this.excludedStatuses.includes(s.value));
    }

    private subStatusCodeOptions: Array<any> = [];

    // SD-1753: Excluded substatus - RxDenied (14)
    private excludedSubstatusId = 14;

    get filteredSubStatusCodeOptions() {
        if (this.showAllStatuses) {
            return this.subStatusCodeOptions;
        }
        return this.subStatusCodeOptions.filter((ss: any) => ss.value !== this.excludedSubstatusId);
    }

    async getSubStatusCodeOptions() {
        try {
            const resp = await this.$http.get("/api/Configuration");
            this.subStatusCodeOptions = resp.data.onHoldSubStatuses.map((ss: any) => ({
                name: ss.name,
                value: ss.id
            }));
        } catch {
            // ignore
        }
    }

    mounted() {
        this.getSubStatusCodeOptions();
        this.load();
    }

    @Watch("endDate")
    handleStartDate() {
        if (this.endDate && !this.startDate)
            this.startDate = this.endDate;
        if (!this.endDate)
            this.startDate = "";
    }

    @Watch("statuses")
    @Watch("substatuses")
    @Watch("storeFilter")
    @Watch("startDate")
    @Watch("endDate")
    @Watch("programFilter")
    resetPageNumber() {
        this.pageNumber = 1;
        this.load();
    }

    @Watch("showAllStatuses")
    handleShowAllStatusesChange() {
        // Clear selected filters when toggling showAllStatuses to avoid invalid selections
        this.statuses = [];
        this.substatuses = [];
        this.pageNumber = 1;
        this.load();
    }

    @Watch("pageNumber")
    load() {
        this.isLoading = true;

        const params = {
            pageSize: this.pageSize,
            pageNumber: this.pageNumber,
            storeId: this.storeFilter?.id,
            statuses: this.statuses.map(s => s.value).join(","),
            substatuses: this.substatuses.map(s => s.value).join(","),
            startDate: this.startDate,
            endDate: this.endDate,
            programId: this.programFilter?.id,
            sortBy: this.sortBy,
            sortDirection: this.sortDirection,
            showAllStatuses: this.showAllStatuses
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });

        this.$http.get<any>('api/Prescription/active-queue', { params: params })
            .then(response => {
                this.prescriptions = response.data.scripts;
                this.totalRows = response.data.totalRows;
            })
            .catch(error => {
                console.error("Error while loading active queue.", { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    handleSort(event: any) {
        this.sortBy = event.sortBy;
        this.sortDirection = event.sortDesc ? "D" : "A";
        this.pageNumber = 1;
        this.load();
    }
}

</script>

<style scoped>
.filter-grid {
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 430px), 1fr));


}

@media (min-width: 1600px) {
    .filter-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}
</style>
